import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import "./App.css";
import Slider from "./components/Slider";
import { useWorker } from "./hooks/useWorker";
import { applyBlend, applyAdjustments, getPreviewDims, getMobilePreviewDims, getExportDims, pixelateRegion } from "./imageUtils";

/* ===================================================
   CONSTANTS
   =================================================== */
const PATTERNS = [
  { id: 'irielle', name: 'irielle', url: '/irielle.png', blendMode: 'linearDodge' },
  { id: 'vesper', name: 'vesper', url: '/vesper.jpg', blendMode: 'multiply' },
  { id: 'clock', name: 'clock', url: '/clock.jpg', blendMode: 'multiply' },
  { id: 'ivory', name: 'ivory', url: '/ivory.jpg', blendMode: 'overlay' },
  { id: 'dahlia', name: 'dahlia', url: '/dahlia.jpg', blendMode: 'multiply' },
  { id: 'lapis', name: 'lapis', url: '/lapis.jpg', blendMode: 'softLight' },
  { id: 'xyze', name: 'xyze', url: '/xyze.jpg', blendMode: 'linearDodge' },
  { id: 'angel', name: 'angel', url: '/angel.jpg', blendMode: 'linearDodge' },
  { id: 'lily', name: 'lily', url: '/lily.jpg', blendMode: 'overlay' },
  { id: 'chrome', name: 'chrome', url: '/chrome.jpg', blendMode: 'overlay' },
  { id: 'whitecedar', name: 'white cedar', url: '/whitecedar.jpg', blendMode: 'softLight' },
  { id: 'eden', name: 'eden', url: '/eden.jpg', blendMode: 'overlay' },
  { id: 'diminuendo', name: 'diminuendo', url: '/diminuendo.jpg', blendMode: 'overlay' }
];

const OVERLAYS = [
  { id: 'lumina', name: 'lumina', url: '/lumina.jpg', blendMode: 'linearDodge' },
  { id: 'blurra', name: 'blurra', url: '/blurra.jpg', blendMode: 'softLight' },
  { id: 'starry', name: 'starry', url: '/starry.png', blendMode: 'linearDodge' },
  { id: 'rainbowdash', name: 'rainbowdash', url: '/ll-rainbowdash.jpg', blendMode: 'linearDodge' },
  { id: 'heaven', name: 'heaven', url: '/ll-heaven.jpg', blendMode: 'linearDodge' }
];

const DIGI = [
  { id: 'moire', name: 'moiré', url: '/moire.jpg', blendMode: 'softLight' },
  { id: 'static', name: 'static lines', url: '/static-overlay.jpg', blendMode: 'overlay' },
  { id: 'analog', name: 'analog', url: '/analog-overlay.jpg', blendMode: 'overlay' },
  { id: 'moire2', name: 'moiré_2', url: '/moire2.jpg', blendMode: 'overlay' },
  { id: 'retroblue', name: 'retro blue', url: '/retro-blue.jpg', blendMode: 'softLight' },
  { id: 'grunge', name: 'grunge', url: '/grunge.jpg', blendMode: 'softLight' },
  { id: 'fadedfilm', name: 'faded film', url: '/faded-film.jpg', blendMode: 'overlay' },
  { id: 'vintage-digi', name: 'vintage', url: '/vintage-digi.jpg', blendMode: 'softLight' },
  { id: 'interference', name: 'interference', url: '/interference.jpg', blendMode: 'softLight' },
  { id: 'burn', name: 'burn', url: '/burn.jpg', blendMode: 'softLight' },
  { id: 'haze', name: 'haze', url: '/haze.jpg', blendMode: 'softLight' },
  { id: 'wash', name: 'wash', url: '/wash.jpg', blendMode: 'screen' }
];


const FILTERS = [
  { id: 'twilight', name: 'twilight', url: '/filter-twilight.jpg', blendMode: 'linearDodge' },
  { id: 'vintage', name: 'vintage', url: '/filter-vintage.jpg', blendMode: 'linearDodge' },
  { id: 'mossy', name: 'mossy', url: '/filter-mossy.jpg', blendMode: 'linearDodge' },
  { id: 'sundots', name: 'sundots', url: '/filter-sundots.jpg', blendMode: 'linearDodge' },
  { id: 'dreamlight', name: 'dreamlight', url: '/filter-dreamlight.jpg', blendMode: 'linearDodge' },
  { id: 'strawberry', name: 'strawberry', url: '/ll-strawberrz.jpg', blendMode: 'linearDodge' }
];

const DESKTOP_ICONS = [
  { src: '/cd_drive-4.png', alt: 'cd drive', label: 'CD Drive' },
  { src: '/recycle_bin_full-4.png', alt: 'recycle bin', label: 'Recycle Bin' },
  { src: '/world-4.png', alt: 'internet', label: 'Internet' },
  { src: '/search_file-2.png', alt: 'search', label: 'Search' },
  { src: '/computer_explorer-4.png', alt: 'my computer', label: 'My Computer' },
];

const MAX_DISPLAY_WIDTH = 700;
const MAX_DISPLAY_HEIGHT = 450;
const MOBILE_MAX_EXPORT_PX = 1200; // Mobile export cap — fast to encode, good for sharing
const MOBILE_WORKING_PX = 800;     // Mobile working resolution — longest side capped here
                                    // 800px = ~480K pixels max. Phone cameras are 12MP (4000×3000 = 12M pixels).
                                    // Downscaling at upload makes every render ~25× faster.
                                    // Still 2× retina quality on phone screens (390px CSS width).
const DEFAULT_HUE = 0;
const DEFAULT_OPACITY = 70;
const DEFAULT_SCALE_X = 100;
const DEFAULT_SCALE_Y = 100;
const DEFAULT_ADJUSTMENTS = {
  exposure: 0, brightness: 0, contrast: 0, vibrance: 0, saturation: 0,
  warmth: 0, tint: 0, sharpness: 0, definition: 0, vignette: 0,
  pixelation: 0, blur: 0
};

const INITIAL_RENDER_PARAMS = {
  activeTab: null,
  selectedPattern: 'irielle',
  selectedOverlay: 'lumina',
  selectedDigi: 'moire',
  selectedFilter: 'twilight',
  patternHue: DEFAULT_HUE, patternOpacity: DEFAULT_OPACITY,
  patternScaleX: DEFAULT_SCALE_X, patternScaleY: DEFAULT_SCALE_Y,
  overlayHue: DEFAULT_HUE, overlayOpacity: DEFAULT_OPACITY,
  overlayScaleX: DEFAULT_SCALE_X, overlayScaleY: DEFAULT_SCALE_Y,
  digiHue: DEFAULT_HUE, digiOpacity: DEFAULT_OPACITY,
  digiScaleX: DEFAULT_SCALE_X, digiScaleY: DEFAULT_SCALE_Y,
  filterHue: DEFAULT_HUE, filterOpacity: DEFAULT_OPACITY,
  adjustments: { ...DEFAULT_ADJUSTMENTS },
  collageMode: 'single', collageDivider: 50,
  collageScaleX: 100, collageScaleY: 100,
  collagePixelA: false, collagePixelB: false, collagePixelSizeA: 10, collagePixelSizeB: 10,
  collageRotate: 0,
};

/* ===================================================
   MAIN APP COMPONENT
   =================================================== */
function App() {
  const [baseImage, setBaseImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [displayDimensions, setDisplayDimensions] = useState({ width: 0, height: 0 });
  const [fileName, setFileName] = useState("");

  const [activeTab, setActiveTab] = useState(null);
  const [selectedPattern, setSelectedPattern] = useState('irielle');
  const [selectedOverlay, setSelectedOverlay] = useState('lumina');
  const [selectedDigi, setSelectedDigi] = useState('moire');
  const [selectedFilter, setSelectedFilter] = useState('twilight');

  const [patternHue, setPatternHue] = useState(DEFAULT_HUE);
  const [patternOpacity, setPatternOpacity] = useState(DEFAULT_OPACITY);
  const [patternScaleX, setPatternScaleX] = useState(DEFAULT_SCALE_X);
  const [patternScaleY, setPatternScaleY] = useState(DEFAULT_SCALE_Y);
  const [overlayHue, setOverlayHue] = useState(DEFAULT_HUE);
  const [overlayOpacity, setOverlayOpacity] = useState(DEFAULT_OPACITY);
  const [overlayScaleX, setOverlayScaleX] = useState(DEFAULT_SCALE_X);
  const [overlayScaleY, setOverlayScaleY] = useState(DEFAULT_SCALE_Y);
  const [digiHue, setDigiHue] = useState(DEFAULT_HUE);
  const [digiOpacity, setDigiOpacity] = useState(DEFAULT_OPACITY);
  const [digiScaleX, setDigiScaleX] = useState(DEFAULT_SCALE_X);
  const [digiScaleY, setDigiScaleY] = useState(DEFAULT_SCALE_Y);
  const [filterHue, setFilterHue] = useState(DEFAULT_HUE);
  const [filterOpacity, setFilterOpacity] = useState(DEFAULT_OPACITY);
  const [adjustments, setAdjustments] = useState({ ...DEFAULT_ADJUSTMENTS });

  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isExporting, setIsExporting] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [clockTime, setClockTime] = useState(() => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

  /* ---- Collage state ---- */
  const [collageMode, setCollageMode] = useState('single'); // 'single' | 'horizontal' | 'vertical'
  const [collageDivider, setCollageDivider] = useState(50);
  const [collageScaleX, setCollageScaleX] = useState(100);
  const [collageScaleY, setCollageScaleY] = useState(100);
  const [collageSecondImage, setCollageSecondImage] = useState(null);
  const [collagePixelA, setCollagePixelA] = useState(false); // left or top
  const [collagePixelB, setCollagePixelB] = useState(false); // right or bottom
  const [collagePixelSizeA, setCollagePixelSizeA] = useState(10);
  const [collagePixelSizeB, setCollagePixelSizeB] = useState(10);
  const [collageRotate, setCollageRotate] = useState(0);
  const collageFirstImgRef = useRef(null);   // side A image (independent from baseImage)
  const collageSecondImgRef = useRef(null);
  const collageFileInputRef = useRef(null);
  const collageUploadSide = useRef('b'); // which side the upload targets: 'a' or 'b'

  const rp = useRef({ ...INITIAL_RENDER_PARAMS });
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const baseImageRef = useRef(null);
  const patternImgsRef = useRef({});
  const overlayImgsRef = useRef({});
  const digiImgsRef = useRef({});
  const filterImgsRef = useRef({});

  /* ---- Performance refs ---- */
  const tempCanvasRef = useRef(document.createElement("canvas"));
  const scaledCacheRef = useRef({ key: '', data: null });
  const rafRef = useRef(null);
  const previewRafRef = useRef(null);
  const renderIdRef = useRef(0);
  const imageDimsRef = useRef({ width: 0, height: 0 });
  const displayDimsRef = useRef({ width: 0, height: 0 });
  /* Reusable offscreen canvases — avoids GC thrashing from creating new ones each frame */
  const previewOffscreenRef = useRef(document.createElement("canvas"));
  const fullResTimerRef = useRef(null);

  /* ---- Offscreen export canvas ----
     Separate from the preview canvas. Download renders to this
     canvas so the visible preview is never mutated during export.
     Reused across downloads (created once). */
  const exportCanvasRef = useRef(document.createElement("canvas"));

  /* ---- Mobile detection ----
     On mobile, slider drag skips all canvas preview renders.
     Only the Slider's local state updates during drag (cheap).
     Full render fires once on pointer release. */
  const isMobileRef = useRef(false);
  useEffect(() => {
    isMobileRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  // Taskbar clock
  useEffect(() => {
    const id = setInterval(() => {
      setClockTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }, 30000);
    return () => clearInterval(id);
  }, []);

  /* ---- Web Worker for pixel processing ---- */
  const { workerRef, processInWorker } = useWorker();

  /* ---- Image loading ---- */
  useEffect(() => {
    const loadSet = (items, targetRef) => {
      items.forEach(item => {
        const img = new Image();
        img.onload = () => { targetRef.current[item.id] = img; };
        img.src = item.url;
      });
    };
    loadSet(PATTERNS, patternImgsRef);
    loadSet(OVERLAYS, overlayImgsRef);
    loadSet(DIGI, digiImgsRef);
    loadSet(FILTERS, filterImgsRef);
  }, []);

  /* ===================================================
     RENDER ENGINE
     
     renderToCanvas: renders current effect state to ANY canvas.
     This is the shared core — used by preview, full render,
     and export. Accepts a canvas element + dimensions.
     =================================================== */
  /* Effect tab → config lookup (replaces if-else chain, fix #4) */
  /* Pre-build id→def maps so renderToCanvas avoids Array.find() on every frame */
  const EFFECT_CONFIG = useRef({
    patterns:   { imgRef: () => patternImgsRef, catalogMap: Object.fromEntries(PATTERNS.map(p => [p.id, p])), selKey: 'selectedPattern', prefix: 'pattern', hasScale: true },
    overlay:    { imgRef: () => overlayImgsRef, catalogMap: Object.fromEntries(OVERLAYS.map(p => [p.id, p])), selKey: 'selectedOverlay', prefix: 'overlay', hasScale: true },
    digi:       { imgRef: () => digiImgsRef,    catalogMap: Object.fromEntries(DIGI.map(p => [p.id, p])),     selKey: 'selectedDigi',    prefix: 'digi',    hasScale: true },
    filters:    { imgRef: () => filterImgsRef,   catalogMap: Object.fromEntries(FILTERS.map(p => [p.id, p])), selKey: 'selectedFilter', prefix: 'filter',  hasScale: false },
  });

  const renderToCanvas = useCallback((canvas, targetW, targetH, useCache) => {
    const baseImg = baseImageRef.current;
    if (!canvas || !baseImg) return;

    const p = rp.current;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = targetW;
    canvas.height = targetH;
    ctx.drawImage(baseImg, 0, 0, targetW, targetH);

    const cfg = EFFECT_CONFIG.current[p.activeTab];
    if (cfg) {
      const effectImg = cfg.imgRef().current[p[cfg.selKey]];
      const effectDef = cfg.catalogMap[p[cfg.selKey]];
      const hue = p[cfg.prefix + 'Hue'];
      const opacity = p[cfg.prefix + 'Opacity'];
      const scaleX = cfg.hasScale ? p[cfg.prefix + 'ScaleX'] : 100;
      const scaleY = cfg.hasScale ? p[cfg.prefix + 'ScaleY'] : 100;

      if (effectImg && effectDef) {
        const tc = tempCanvasRef.current;
        const cacheKey = `${effectDef.id}_${scaleX}_${scaleY}_${targetW}_${targetH}`;

        let overlayData;
        if (useCache && scaledCacheRef.current.key === cacheKey) {
          overlayData = scaledCacheRef.current.data;
        } else {
          tc.width = targetW;
          tc.height = targetH;
          const tCtx = tc.getContext("2d", { willReadFrequently: true });
          tCtx.clearRect(0, 0, targetW, targetH);
          if (cfg.hasScale) {
            const sw = Math.round(targetW * scaleX / 100);
            const sh = Math.round(targetH * scaleY / 100);
            tCtx.drawImage(effectImg, (targetW - sw) / 2, (targetH - sh) / 2, sw, sh);
          } else {
            tCtx.drawImage(effectImg, 0, 0, targetW, targetH);
          }
          overlayData = tCtx.getImageData(0, 0, targetW, targetH);
          if (useCache) scaledCacheRef.current = { key: cacheKey, data: overlayData };
        }

        const baseData = ctx.getImageData(0, 0, targetW, targetH);
        applyBlend(baseData.data, overlayData.data, { hue, opacity, blendMode: effectDef.blendMode });
        ctx.putImageData(baseData, 0, 0);
      }
    }

    if (p.activeTab === 'adjust') {
      const a = p.adjustments;
      if (Object.values(a).some(v => v !== 0)) {
        const baseData = ctx.getImageData(0, 0, targetW, targetH);
        const ctxHandled = applyAdjustments(baseData.data, targetW, targetH, a, ctx);
        if (!ctxHandled) {
          ctx.putImageData(baseData, 0, 0);
        }
      }
    }

    /* ---- Collage rendering ---- */
    if (p.activeTab === 'collage') {
      const mode = p.collageMode;
      const firstImg = collageFirstImgRef.current || baseImg;
      const secondImg = collageSecondImgRef.current || baseImg;

      if (mode === 'single') {
        const sx = p.collageScaleX / 100;
        const sy = p.collageScaleY / 100;
        const angle = p.collageRotate * Math.PI / 180;

        // Scale the canvas output dimensions
        const cw = Math.max(1, Math.round(targetW * sx));
        const ch = Math.max(1, Math.round(targetH * sy));
        canvas.width = cw;
        canvas.height = ch;

        // Auto-scale for rotation: ensure rotated image fills the SCALED canvas
        const cosA = Math.abs(Math.cos(angle));
        const sinA = Math.abs(Math.sin(angle));
        const autoScale = Math.max(
          (cw * cosA + ch * sinA) / (cw || 1),
          (cw * sinA + ch * cosA) / (ch || 1)
        );
        const finalW = cw * autoScale;
        const finalH = ch * autoScale;

        ctx.save();
        ctx.translate(cw / 2, ch / 2);
        ctx.rotate(angle);
        ctx.drawImage(baseImg, -finalW / 2, -finalH / 2, finalW, finalH);
        ctx.restore();
      } else {
        const firstImg = collageFirstImgRef.current || baseImg;
        const secondImg = collageSecondImgRef.current || baseImg;

        if (mode === 'horizontal') {
          const div = Math.round(targetW * p.collageDivider / 100);
          if (div > 0) ctx.drawImage(firstImg, 0, 0, div, targetH);
          if (targetW - div > 0) ctx.drawImage(secondImg, div, 0, targetW - div, targetH);
          if (p.collagePixelA && div > 0) pixelateRegion(ctx, 0, 0, div, targetH, p.collagePixelSizeA);
          if (p.collagePixelB && targetW - div > 0) pixelateRegion(ctx, div, 0, targetW - div, targetH, p.collagePixelSizeB);
        } else if (mode === 'vertical') {
          const div = Math.round(targetH * p.collageDivider / 100);
          if (div > 0) ctx.drawImage(firstImg, 0, 0, targetW, div);
          if (targetH - div > 0) ctx.drawImage(secondImg, 0, div, targetW, targetH - div);
          if (p.collagePixelA && div > 0) pixelateRegion(ctx, 0, 0, targetW, div, p.collagePixelSizeA);
          if (p.collagePixelB && targetH - div > 0) pixelateRegion(ctx, 0, div, targetW, targetH - div, p.collagePixelSizeB);
        }
      }
    }
  }, []);

  // Render to visible preview canvas at full image dimensions
  const doRender = useCallback(() => {
    const dims = imageDimsRef.current;
    if (!dims.width || !canvasRef.current) return;
    renderToCanvas(canvasRef.current, dims.width, dims.height, true);
  }, [renderToCanvas]);

  // Render to visible preview canvas at reduced dimensions (drag preview — desktop)
  // Reuses previewOffscreenRef to avoid GC pressure
  const doPreviewRender = useCallback(() => {
    const dims = imageDimsRef.current;
    if (!dims.width || !canvasRef.current) return;
    const pd = getPreviewDims(dims.width, dims.height);
    const offscreen = previewOffscreenRef.current;
    offscreen.width = pd.width;
    offscreen.height = pd.height;
    renderToCanvas(offscreen, pd.width, pd.height, false);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = dims.width;
    canvas.height = dims.height;
    ctx.drawImage(offscreen, 0, 0, dims.width, dims.height);
  }, [renderToCanvas]);

  // Mobile drag preview: ultra-low-res for instant feedback (~60K pixels)
  const doMobilePreviewRender = useCallback(() => {
    const dims = imageDimsRef.current;
    if (!dims.width || !canvasRef.current) return;
    const pd = getMobilePreviewDims(dims.width, dims.height);
    const offscreen = previewOffscreenRef.current;
    offscreen.width = pd.width;
    offscreen.height = pd.height;
    renderToCanvas(offscreen, pd.width, pd.height, false);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = dims.width;
    canvas.height = dims.height;
    ctx.drawImage(offscreen, 0, 0, dims.width, dims.height);
  }, [renderToCanvas]);

  /* ---- Async render via Web Worker ----
     Offloads pixel processing (blend + adjustments) to the worker.
     Canvas drawing (drawImage) stays on main thread (GPU-accelerated).
     Falls back to sync renderToCanvas for collage or if worker unavailable. */
  const renderToCanvasAsync = useCallback(async (canvas, targetW, targetH) => {
    const baseImg = baseImageRef.current;
    if (!canvas || !baseImg) return;

    const p = rp.current;
    const cfg = EFFECT_CONFIG.current[p.activeTab];
    const isBlendTab = !!cfg;
    const isAdjustTab = p.activeTab === 'adjust';

    // Only use worker for blend/adjust tabs when worker is available
    if (!(isBlendTab || isAdjustTab) || !workerRef.current) {
      renderToCanvas(canvas, targetW, targetH, false);
      return;
    }

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = targetW;
    canvas.height = targetH;
    ctx.drawImage(baseImg, 0, 0, targetW, targetH);

    let blendCfg = null;
    let overlayBuf = null;

    if (cfg) {
      const effectImg = cfg.imgRef().current[p[cfg.selKey]];
      const effectDef = cfg.catalogMap[p[cfg.selKey]];
      if (effectImg && effectDef) {
        const tc = tempCanvasRef.current;
        const scaleX = cfg.hasScale ? p[cfg.prefix + 'ScaleX'] : 100;
        const scaleY = cfg.hasScale ? p[cfg.prefix + 'ScaleY'] : 100;
        tc.width = targetW; tc.height = targetH;
        const tCtx = tc.getContext("2d", { willReadFrequently: true });
        tCtx.clearRect(0, 0, targetW, targetH);
        if (cfg.hasScale) {
          const sw = Math.round(targetW * scaleX / 100);
          const sh = Math.round(targetH * scaleY / 100);
          tCtx.drawImage(effectImg, (targetW - sw) / 2, (targetH - sh) / 2, sw, sh);
        } else {
          tCtx.drawImage(effectImg, 0, 0, targetW, targetH);
        }
        const ovData = tCtx.getImageData(0, 0, targetW, targetH);
        overlayBuf = ovData.data.buffer.slice(0);
        blendCfg = { hue: p[cfg.prefix + 'Hue'], opacity: p[cfg.prefix + 'Opacity'], blendMode: effectDef.blendMode };
      }
    }

    let adjCfg = null;
    if (isAdjustTab) {
      const a = p.adjustments;
      if (Object.values(a).some(v => v !== 0)) adjCfg = { ...a };
    }

    if (!blendCfg && !adjCfg) return;

    const baseData = ctx.getImageData(0, 0, targetW, targetH);
    const resultBuffer = await processInWorker({
      type: 'process',
      data: {
        baseBuffer: baseData.data.buffer,
        overlayBuffer: overlayBuf,
        width: targetW, height: targetH,
        blend: blendCfg,
        adj: adjCfg ? { ...adjCfg, blur: 0 } : null // blur needs canvas ctx (GPU-accel)
      }
    });

    if (resultBuffer) {
      ctx.putImageData(new ImageData(new Uint8ClampedArray(resultBuffer), targetW, targetH), 0, 0);
      // GPU-accelerated blur on main thread
      if (adjCfg && adjCfg.blur > 0) {
        ctx.filter = `blur(${adjCfg.blur * 0.2}px)`;
        ctx.drawImage(canvas, 0, 0);
        ctx.filter = 'none';
      }
    }
  }, [renderToCanvas, processInWorker]);

  /* ---- Render when baseImage changes ----
     Fixes first-upload bug: when baseImage goes from null to a value,
     React needs to commit the DOM (adding the <canvas> element) before
     we can render to it. requestAnimationFrame in the upload handler
     fires before React's commit phase. useEffect runs AFTER the DOM
     update, so canvasRef.current is guaranteed to exist. */
  useEffect(() => {
    if (baseImage && canvasRef.current && imageDimsRef.current.width) {
      // Recalculate display dims now that container is mounted and measurable
      const { width, height } = imageDimsRef.current;
      const newDisplay = calculateDisplayDimensions(width, height, isMaximized);
      setDisplayDimensions(newDisplay);
      displayDimsRef.current = newDisplay;
      renderToCanvas(canvasRef.current, width, height, true);
    }
  }, [baseImage, renderToCanvas]); // eslint-disable-line react-hooks/exhaustive-deps
  const scheduleRender = useCallback(() => {
    if (previewRafRef.current) cancelAnimationFrame(previewRafRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (fullResTimerRef.current) clearTimeout(fullResTimerRef.current);
    const id = ++renderIdRef.current;

    const dims = imageDimsRef.current;
    if (dims.width && canvasRef.current) {
      // Phase 1: Instant low-res preview (sync)
      const pd = getPreviewDims(dims.width, dims.height);
      const offscreen = previewOffscreenRef.current;
      offscreen.width = pd.width;
      offscreen.height = pd.height;
      renderToCanvas(offscreen, pd.width, pd.height, false);

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      // Read the actual offscreen dimensions (renderToCanvas may have resized for collage)
      canvas.width = offscreen.width;
      canvas.height = offscreen.height;
      ctx.drawImage(offscreen, 0, 0);
    }

    // Phase 2: Full-res render — debounced, renders to OFFSCREEN canvas first
    fullResTimerRef.current = setTimeout(() => {
      if (id !== renderIdRef.current) return;
      rafRef.current = requestAnimationFrame(async () => {
        if (id !== renderIdRef.current) return;
        if (!dims.width || !canvasRef.current) return;
        const ec = exportCanvasRef.current;
        if (workerRef.current) {
          await renderToCanvasAsync(ec, dims.width, dims.height);
        } else {
          renderToCanvas(ec, dims.width, dims.height, false);
        }
        if (id !== renderIdRef.current || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = ec.width;
        canvas.height = ec.height;
        ctx.drawImage(ec, 0, 0);
      });
    }, 80);
  }, [renderToCanvas, renderToCanvasAsync]);

  const schedulePreview = useCallback(() => {
    if (previewRafRef.current) cancelAnimationFrame(previewRafRef.current);
    if (fullResTimerRef.current) clearTimeout(fullResTimerRef.current);
    // Render immediately (no rAF delay) for instant slider response
    if (isMobileRef.current) doMobilePreviewRender();
    else doPreviewRender();
  }, [doPreviewRender, doMobilePreviewRender]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (previewRafRef.current) cancelAnimationFrame(previewRafRef.current);
      if (fullResTimerRef.current) clearTimeout(fullResTimerRef.current);
    };
  }, []);

  /* ===================================================
     HANDLER FACTORY — stable drag + commit callbacks.
     drag: both mobile and desktop get live preview.
       Mobile: ultra-low-res (~60K px) for instant feedback.
       Desktop: standard low-res (~250K px).
     commit: always triggers full-res render on release.
     =================================================== */
  const h = useMemo(() => {
    const make = (refKey, setter, invalidateCache = false) => ({
      drag: (v) => {
        rp.current[refKey] = v;
        if (invalidateCache) scaledCacheRef.current = { key: '', data: null };
        schedulePreview();
      },
      commit: (v) => {
        rp.current[refKey] = v;
        setter(v);
        if (invalidateCache) scaledCacheRef.current = { key: '', data: null };
        scheduleRender();
      }
    });

    const result = {
      patternHue: make('patternHue', setPatternHue),
      patternOpacity: make('patternOpacity', setPatternOpacity),
      patternScaleX: make('patternScaleX', setPatternScaleX, true),
      patternScaleY: make('patternScaleY', setPatternScaleY, true),
      overlayHue: make('overlayHue', setOverlayHue),
      overlayOpacity: make('overlayOpacity', setOverlayOpacity),
      overlayScaleX: make('overlayScaleX', setOverlayScaleX, true),
      overlayScaleY: make('overlayScaleY', setOverlayScaleY, true),
      digiHue: make('digiHue', setDigiHue),
      digiOpacity: make('digiOpacity', setDigiOpacity),
      digiScaleX: make('digiScaleX', setDigiScaleX, true),
      digiScaleY: make('digiScaleY', setDigiScaleY, true),
      filterHue: make('filterHue', setFilterHue),
      filterOpacity: make('filterOpacity', setFilterOpacity),
      adj: {}
    };

    Object.keys(DEFAULT_ADJUSTMENTS).forEach(key => {
      result.adj[key] = {
        drag: (val) => {
          rp.current.adjustments[key] = val;
          schedulePreview();
        },
        commit: (val) => {
          rp.current.adjustments[key] = val;
          setAdjustments({ ...rp.current.adjustments });
          scheduleRender();
        }
      };
    });

    return result;
  }, [schedulePreview, scheduleRender]);

  /* ---- Collage handlers ---- */
  const hCollage = useMemo(() => ({
    divider: {
      drag: (v) => { rp.current.collageDivider = v; schedulePreview(); },
      commit: (v) => { rp.current.collageDivider = v; setCollageDivider(v); scheduleRender(); },
    },
    scaleX: {
      drag: (v) => {
        rp.current.collageScaleX = v;
        // Live-update CSS display width (bypasses React re-render for perf)
        const c = canvasRef.current;
        if (c && rp.current.collageMode === 'single') {
          c.style.width = Math.round(displayDimsRef.current.width * v / 100) + 'px';
        }
        schedulePreview();
      },
      commit: (v) => { rp.current.collageScaleX = v; setCollageScaleX(v); scheduleRender(); },
    },
    scaleY: {
      drag: (v) => {
        rp.current.collageScaleY = v;
        const c = canvasRef.current;
        if (c && rp.current.collageMode === 'single') {
          c.style.height = Math.round(displayDimsRef.current.height * v / 100) + 'px';
        }
        schedulePreview();
      },
      commit: (v) => { rp.current.collageScaleY = v; setCollageScaleY(v); scheduleRender(); },
    },
    pixelSizeA: {
      drag: (v) => { rp.current.collagePixelSizeA = v; schedulePreview(); },
      commit: (v) => { rp.current.collagePixelSizeA = v; setCollagePixelSizeA(v); scheduleRender(); },
    },
    pixelSizeB: {
      drag: (v) => { rp.current.collagePixelSizeB = v; schedulePreview(); },
      commit: (v) => { rp.current.collagePixelSizeB = v; setCollagePixelSizeB(v); scheduleRender(); },
    },
    rotate: {
      drag: (v) => { rp.current.collageRotate = v; schedulePreview(); },
      commit: (v) => { rp.current.collageRotate = v; setCollageRotate(v); scheduleRender(); },
    },
  }), [schedulePreview, scheduleRender]);

  /* ---- Reset helpers ---- */
  const resetPatternControls = useCallback(() => {
    setPatternHue(DEFAULT_HUE); setPatternOpacity(DEFAULT_OPACITY);
    setPatternScaleX(DEFAULT_SCALE_X); setPatternScaleY(DEFAULT_SCALE_Y);
    rp.current.patternHue = DEFAULT_HUE; rp.current.patternOpacity = DEFAULT_OPACITY;
    rp.current.patternScaleX = DEFAULT_SCALE_X; rp.current.patternScaleY = DEFAULT_SCALE_Y;
  }, []);
  const resetOverlayControls = useCallback(() => {
    setOverlayHue(DEFAULT_HUE); setOverlayOpacity(DEFAULT_OPACITY);
    setOverlayScaleX(DEFAULT_SCALE_X); setOverlayScaleY(DEFAULT_SCALE_Y);
    rp.current.overlayHue = DEFAULT_HUE; rp.current.overlayOpacity = DEFAULT_OPACITY;
    rp.current.overlayScaleX = DEFAULT_SCALE_X; rp.current.overlayScaleY = DEFAULT_SCALE_Y;
  }, []);
  const resetDigiControls = useCallback(() => {
    setDigiHue(DEFAULT_HUE); setDigiOpacity(DEFAULT_OPACITY);
    setDigiScaleX(DEFAULT_SCALE_X); setDigiScaleY(DEFAULT_SCALE_Y);
    rp.current.digiHue = DEFAULT_HUE; rp.current.digiOpacity = DEFAULT_OPACITY;
    rp.current.digiScaleX = DEFAULT_SCALE_X; rp.current.digiScaleY = DEFAULT_SCALE_Y;
  }, []);
  const resetFilterControls = useCallback(() => {
    setFilterHue(DEFAULT_HUE); setFilterOpacity(DEFAULT_OPACITY);
    rp.current.filterHue = DEFAULT_HUE; rp.current.filterOpacity = DEFAULT_OPACITY;
  }, []);
  const resetAdjustments = useCallback(() => {
    const fresh = { ...DEFAULT_ADJUSTMENTS };
    setAdjustments(fresh);
    rp.current.adjustments = fresh;
  }, []);

  const resetCollageControls = useCallback(() => {
    setCollageDivider(50); setCollageScaleX(100); setCollageScaleY(100);
    setCollageSecondImage(null); setCollagePixelA(false); setCollagePixelB(false);
    setCollagePixelSizeA(10); setCollagePixelSizeB(10); setCollageRotate(0);
    setCollageMode('single');
    collageFirstImgRef.current = null;
    collageSecondImgRef.current = null;
    rp.current.collageDivider = 50;
    rp.current.collageScaleX = 100; rp.current.collageScaleY = 100;
    rp.current.collagePixelA = false; rp.current.collagePixelB = false;
    rp.current.collagePixelSizeA = 10; rp.current.collagePixelSizeB = 10;
    rp.current.collageRotate = 0;
    rp.current.collageMode = 'single';
  }, []);
  const resetAllControls = useCallback(() => {
    resetPatternControls(); resetOverlayControls(); resetDigiControls();
    resetFilterControls(); resetAdjustments(); resetCollageControls();
    setSelectedPattern('irielle'); setSelectedOverlay('lumina'); setSelectedDigi('moire');
    setSelectedFilter('twilight');
    rp.current.selectedPattern = 'irielle';
    rp.current.selectedOverlay = 'lumina';
    rp.current.selectedDigi = 'moire';
    rp.current.selectedFilter = 'twilight';
    scaledCacheRef.current = { key: '', data: null };
  }, [resetPatternControls, resetOverlayControls, resetDigiControls, resetFilterControls, resetAdjustments, resetCollageControls]);

  /* ---- Image upload ----
     On mobile: downscale the image to MOBILE_WORKING_PX at upload time.
     This makes ALL subsequent renders fast (blending ~480K pixels vs 12M).
     The downscaled image becomes the working image AND the base for history.
     On desktop: use full resolution (desktop GPUs handle it fine). */
  const calculateDisplayDimensions = (w, ht, maximized) => {
    const isMobile = isMobileRef.current;
    
    if (isMobile) {
      const vw = window.innerWidth - 16;
      const maxH = Math.floor(vw * 6 / 7);
      const r = Math.min(vw / w, maxH / ht, 1);
      return { width: Math.round(w * r), height: Math.round(ht * r) };
    }

    let maxW, maxH;
    if (maximized) {
      // 3-column layout: 260px options | flex center | 320px sliders
      maxW = window.innerWidth - 260 - 320 - 20;
      maxH = window.innerHeight - 180; // title + toolbar + action bar + status bar
    } else {
      // 2-column layout: canvas area | 320px sliders
      maxW = Math.min(MAX_DISPLAY_WIDTH, window.innerWidth - 320 - 80);
      maxH = MAX_DISPLAY_HEIGHT;
    }
    
    const r = Math.min(maxW / w, maxH / ht);
    return { width: Math.round(w * r), height: Math.round(ht * r) };
  };

  const getMinImagePx = () => {
    const screenW = window.screen.width || 1920;
    const screenH = window.screen.height || 1080;
    const screenLongest = Math.max(screenW, screenH);
    // Use ~60% of screen longest side as minimum, capped between 800-1600
    const base = Math.round(screenLongest * 0.6);
    return Math.max(800, Math.min(1600, base));
  };

  /* ---- Shared setup after upload/rescale ---- */
  const initializeImage = (imgEl, dataUrl, w, h) => {
    const dims = { width: w, height: h };
    setImageDimensions(dims);
    imageDimsRef.current = dims;
    setDisplayDimensions(calculateDisplayDimensions(w, h, false));
    baseImageRef.current = imgEl;
    setBaseImage(dataUrl);
    setOriginalImage(dataUrl);
    setActiveTab(null);
    rp.current.activeTab = null;
    resetAllControls();
    setHistory([dataUrl]);
    setHistoryIndex(0);
    scaledCacheRef.current = { key: '', data: null };
  };

  const rescaleAndInit = (sourceImg, targetW, targetH, smooth) => {
    const c = exportCanvasRef.current;
    c.width = targetW;
    c.height = targetH;
    const ctx = c.getContext("2d");
    if (smooth) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
    }
    ctx.drawImage(sourceImg, 0, 0, targetW, targetH);
    const dataUrl = c.toDataURL("image/png");
    const newImg = new Image();
    newImg.onload = () => initializeImage(newImg, dataUrl, targetW, targetH);
    newImg.src = dataUrl;
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const isMobile = isMobileRef.current;
        const longest = Math.max(img.width, img.height);

        if (isMobile && longest > MOBILE_WORKING_PX) {
          const scale = MOBILE_WORKING_PX / longest;
          rescaleAndInit(img, Math.round(img.width * scale), Math.round(img.height * scale), false);
        } else if (longest < getMinImagePx()) {
          const scale = getMinImagePx() / longest;
          rescaleAndInit(img, Math.round(img.width * scale), Math.round(img.height * scale), true);
        } else {
          initializeImage(img, ev.target.result, img.width, img.height);
        }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Recalculate display dimensions when maximize/minimize toggled
  useEffect(() => {
    if (imageDimsRef.current.width > 0) {
      const { width, height } = imageDimsRef.current;
      setDisplayDimensions(calculateDisplayDimensions(width, height, isMaximized));
      // Recalculate after layout settles (instant transition, just need next frame)
      const raf = requestAnimationFrame(() => {
        setDisplayDimensions(calculateDisplayDimensions(width, height, isMaximized));
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isMaximized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep displayDimsRef in sync (readable from drag handlers without stale closures)
  useEffect(() => {
    displayDimsRef.current = displayDimensions;
  }, [displayDimensions]);

  // Recalculate on window resize
  useEffect(() => {
    const onResize = () => {
      if (imageDimsRef.current.width > 0) {
        const { width, height } = imageDimsRef.current;
        setDisplayDimensions(calculateDisplayDimensions(width, height, isMaximized));
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMaximized]); // eslint-disable-line react-hooks/exhaustive-deps


  /* ---- Selection & tab handlers ---- */
  const handleSelectPattern = useCallback((id) => {
    setSelectedPattern(id); rp.current.selectedPattern = id;
    resetPatternControls(); scaledCacheRef.current = { key: '', data: null };
    scheduleRender();
  }, [resetPatternControls, scheduleRender]);
  const handleSelectOverlay = useCallback((id) => {
    setSelectedOverlay(id); rp.current.selectedOverlay = id;
    resetOverlayControls(); scaledCacheRef.current = { key: '', data: null };
    scheduleRender();
  }, [resetOverlayControls, scheduleRender]);
  const handleSelectDigi = useCallback((id) => {
    setSelectedDigi(id); rp.current.selectedDigi = id;
    resetDigiControls(); scaledCacheRef.current = { key: '', data: null };
    scheduleRender();
  }, [resetDigiControls, scheduleRender]);
  const handleSelectFilter = useCallback((id) => {
    setSelectedFilter(id); rp.current.selectedFilter = id;
    resetFilterControls(); scaledCacheRef.current = { key: '', data: null };
    scheduleRender();
  }, [resetFilterControls, scheduleRender]);
  const handleTabSwitch = useCallback((tab) => {
    const newTab = activeTab === tab ? null : tab;
    setActiveTab(newTab); rp.current.activeTab = newTab;
    scaledCacheRef.current = { key: '', data: null };
    scheduleRender();
  }, [activeTab, scheduleRender]);

  /* ===================================================
     APPLY CHANGES — bake current effect into base image.
     Renders to offscreen export canvas so the preview
     canvas is undisturbed during the toDataURL call.
     =================================================== */
  const handleApplyChanges = useCallback(async () => {
    if (!baseImage || !rp.current.activeTab) return;

    if (previewRafRef.current) cancelAnimationFrame(previewRafRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const dims = imageDimsRef.current;
    const ec = exportCanvasRef.current;
    await renderToCanvasAsync(ec, dims.width, dims.height);

    // Read actual output dimensions (collage may have resized the canvas)
    const outW = ec.width;
    const outH = ec.height;

    const newDataUrl = ec.toDataURL("image/png");
    const newImg = new Image();
    newImg.onload = () => {
      baseImageRef.current = newImg;
      setBaseImage(newDataUrl);
      // Update image dimensions if collage changed them
      if (outW !== dims.width || outH !== dims.height) {
        const newDims = { width: outW, height: outH };
        setImageDimensions(newDims);
        imageDimsRef.current = newDims;
        setDisplayDimensions(calculateDisplayDimensions(outW, outH, isMaximized));
      }
      setHistory(prev => {
        const next = prev.slice(0, historyIndex + 1);
        next.push(newDataUrl);
        if (next.length > 50) next.shift();
        setHistoryIndex(next.length - 1);
        return next;
      });
      resetAllControls();
      setActiveTab(null);
      rp.current.activeTab = null;
      scaledCacheRef.current = { key: '', data: null };
      requestAnimationFrame(() => {
        const d = imageDimsRef.current;
        if (canvasRef.current) renderToCanvas(canvasRef.current, d.width, d.height, true);
      });
    };
    newImg.src = newDataUrl;
  }, [baseImage, historyIndex, isMaximized, renderToCanvas, renderToCanvasAsync, resetAllControls]);

  /* ===================================================
     DOWNLOAD — Offscreen Export Canvas Pipeline
     
     1. Cancel pending renders (prevent race conditions)
     2. Determine export dimensions (capped on mobile)
     3. Render to offscreen export canvas (NOT the preview canvas)
     4. Use toBlob() for efficient export (preferred)
     5. Trigger download with robust cross-browser anchor+click
     6. Falls back to toDataURL if toBlob fails or is unavailable
     
     The visible preview canvas is never mutated during export.
     =================================================== */
  const triggerDownload = useCallback((canvas) => {
    // Robust download trigger using toBlob (async, memory-efficient)
    // with toDataURL fallback for browsers where toBlob fails.
    if (canvas.toBlob) {
      canvas.toBlob((blob) => {
        if (!blob) {
          // toBlob returned null — fall back to toDataURL
          triggerDownloadDataURL(canvas);
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "lightleakz-edit.png";
        a.style.display = "none";
        document.body.appendChild(a);
        // dispatchEvent with a full MouseEvent is more reliable than .click()
        // across desktop browsers (Chrome, Firefox, Safari, Edge)
        a.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
        // Delay cleanup so browser can start the download
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 1000);
        setIsExporting(false);
      }, "image/png");
    } else {
      triggerDownloadDataURL(canvas);
    }
  }, []);

  const triggerDownloadDataURL = useCallback((canvas) => {
    // Synchronous fallback — heavier on memory but stays in gesture context
    try {
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "lightleakz-edit.png";
      a.style.display = "none";
      document.body.appendChild(a);
      a.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
      setTimeout(() => document.body.removeChild(a), 500);
    } catch (e) {
      // Last resort: open in new tab so user can right-click save
      const dataUrl = canvas.toDataURL("image/png");
      window.open(dataUrl, "_blank");
    }
    setIsExporting(false);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!baseImage) return;

    if (previewRafRef.current) cancelAnimationFrame(previewRafRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    setIsExporting(true);

    const dims = imageDimsRef.current;
    const expDims = getExportDims(dims.width, dims.height, isMobileRef.current, MOBILE_MAX_EXPORT_PX);

    const ec = exportCanvasRef.current;
    await renderToCanvasAsync(ec, expDims.width, expDims.height);

    triggerDownload(ec);
  }, [baseImage, renderToCanvasAsync, triggerDownload]);

  /* ---- Undo / Redo / Reset ---- */
  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return;
    const prevIdx = historyIndex - 1;
    const img = new Image();
    img.onload = () => {
      baseImageRef.current = img;
      setBaseImage(history[prevIdx]);
      setHistoryIndex(prevIdx);
      // Restore dimensions from the image itself (may differ after collage scale)
      const newDims = { width: img.width, height: img.height };
      setImageDimensions(newDims);
      imageDimsRef.current = newDims;
      setDisplayDimensions(calculateDisplayDimensions(img.width, img.height, isMaximized));
      setActiveTab(null); rp.current.activeTab = null;
      resetAllControls(); scaledCacheRef.current = { key: '', data: null };
      scheduleRender();
    };
    img.src = history[prevIdx];
  }, [historyIndex, history, isMaximized, resetAllControls, scheduleRender]);

  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    const nextIdx = historyIndex + 1;
    const img = new Image();
    img.onload = () => {
      baseImageRef.current = img;
      setBaseImage(history[nextIdx]);
      setHistoryIndex(nextIdx);
      const newDims = { width: img.width, height: img.height };
      setImageDimensions(newDims);
      imageDimsRef.current = newDims;
      setDisplayDimensions(calculateDisplayDimensions(img.width, img.height, isMaximized));
      setActiveTab(null); rp.current.activeTab = null;
      resetAllControls(); scaledCacheRef.current = { key: '', data: null };
      scheduleRender();
    };
    img.src = history[nextIdx];
  }, [historyIndex, history, isMaximized, resetAllControls, scheduleRender]);

  const handleResetCurrentTab = useCallback(() => {
    if (!activeTab) return;
    if (activeTab === 'patterns') resetPatternControls();
    else if (activeTab === 'overlay') resetOverlayControls();
    else if (activeTab === 'digi') resetDigiControls();
    else if (activeTab === 'filters') resetFilterControls();
    else if (activeTab === 'adjust') resetAdjustments();
    else if (activeTab === 'collage') resetCollageControls();
    scaledCacheRef.current = { key: '', data: null };
    scheduleRender();
  }, [activeTab, resetPatternControls, resetOverlayControls, resetDigiControls, resetFilterControls, resetAdjustments, resetCollageControls, scheduleRender]);

  const handleReset = useCallback(() => {
    if (!originalImage) return;
    const img = new Image();
    img.onload = () => {
      baseImageRef.current = img;
      setBaseImage(originalImage);
      setActiveTab(null); rp.current.activeTab = null;
      resetAllControls();
      // Push to history so user can undo back
      setHistory(prev => {
        const next = prev.slice(0, historyIndex + 1);
        next.push(originalImage);
        if (next.length > 50) next.shift();
        setHistoryIndex(next.length - 1);
        return next;
      });
      scaledCacheRef.current = { key: '', data: null };
      scheduleRender();
    };
    img.src = originalImage;
  }, [originalImage, historyIndex, resetAllControls, scheduleRender]);

  const handleClose = useCallback(() => {
    setBaseImage(null); setOriginalImage(null); setActiveTab(null);
    rp.current = { ...INITIAL_RENDER_PARAMS };
    resetAllControls(); setFileName("");
    setHistory([]); setHistoryIndex(-1);
    scaledCacheRef.current = { key: '', data: null };
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [resetAllControls]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;
  const canApply = baseImage && activeTab !== null;

  /* ---- Effective display dimensions (accounts for collage single scale) ---- */
  const isCollageSingle = activeTab === 'collage' && collageMode === 'single';
  const effectiveDisplayDims = useMemo(() => {
    if (isCollageSingle) {
      return {
        width: Math.round(displayDimensions.width * collageScaleX / 100),
        height: Math.round(displayDimensions.height * collageScaleY / 100),
      };
    }
    return displayDimensions;
  }, [isCollageSingle, collageScaleX, collageScaleY, displayDimensions]);

  /* ---- Collage mode handlers ---- */
  const handleCollageMode = useCallback((mode) => {
    setCollageMode(mode);
    rp.current.collageMode = mode;
    setCollageDivider(50); rp.current.collageDivider = 50;
    setCollagePixelA(false); setCollagePixelB(false);
    rp.current.collagePixelA = false; rp.current.collagePixelB = false;
    scheduleRender();
  }, [scheduleRender]);

  const handleCollageSecondUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) return;
    const side = collageUploadSide.current;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        if (side === 'a') {
          collageFirstImgRef.current = img;
        } else {
          collageSecondImgRef.current = img;
          setCollageSecondImage(ev.target.result);
        }
        scheduleRender();
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [scheduleRender]);

  const handleCollageUploadSide = useCallback((side) => {
    collageUploadSide.current = side;
    collageFileInputRef.current?.click();
  }, []);

  const handleCollagePixelToggle = useCallback((side) => {
    if (side === 'a') {
      const next = !collagePixelA;
      setCollagePixelA(next); rp.current.collagePixelA = next;
    } else {
      const next = !collagePixelB;
      setCollagePixelB(next); rp.current.collagePixelB = next;
    }
    scheduleRender();
  }, [collagePixelA, collagePixelB, scheduleRender]);

  /* ===================================================
     OPTION BUTTONS PANEL — rendered separately so it can
     be placed ABOVE the canvas on mobile via CSS order.
     =================================================== */
  /* Option buttons tab config lookup */
  const optionTabConfig = useMemo(() => ({
    patterns:   { items: PATTERNS,   selected: selectedPattern, onSelect: handleSelectPattern, legend: 'choose pattern',    testPrefix: 'pattern' },
    overlay:    { items: OVERLAYS,   selected: selectedOverlay, onSelect: handleSelectOverlay, legend: 'choose overlay',    testPrefix: 'overlay' },
    digi:       { items: DIGI,       selected: selectedDigi,    onSelect: handleSelectDigi,    legend: 'choose digi',       testPrefix: 'digi' },
    filters:    { items: FILTERS,    selected: selectedFilter,  onSelect: handleSelectFilter,  legend: 'choose filter',     testPrefix: 'filter' },
  }), [selectedPattern, selectedOverlay, selectedDigi, selectedFilter,
       handleSelectPattern, handleSelectOverlay, handleSelectDigi, handleSelectFilter]);

  const renderOptionButtons = () => {
    const cfg = optionTabConfig[activeTab];

    if (activeTab === 'collage') {
      return (
        <div className="options-panel" data-testid="options-panel">
          <fieldset className="control-fieldset buttons-fieldset">
            <legend>choose layout</legend>
            <div className="option-buttons collage-layout-buttons">
              <button
                className={`option-btn collage-icon-btn ${collageMode === 'single' ? 'active' : ''}`}
                data-testid="collage-btn-single"
                onClick={() => handleCollageMode('single')}
                title="Single"
              >
                <svg width="24" height="24" viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/></svg>
              </button>
              <button
                className={`option-btn collage-icon-btn ${collageMode === 'horizontal' ? 'active' : ''}`}
                data-testid="collage-btn-horizontal"
                onClick={() => handleCollageMode('horizontal')}
                title="Horizontal (Left/Right)"
              >
                <svg width="24" height="24" viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/><line x1="12" y1="2" x2="12" y2="22" stroke="currentColor" strokeWidth="2"/></svg>
              </button>
              <button
                className={`option-btn collage-icon-btn ${collageMode === 'vertical' ? 'active' : ''}`}
                data-testid="collage-btn-vertical"
                onClick={() => handleCollageMode('vertical')}
                title="Vertical (Top/Bottom)"
              >
                <svg width="24" height="24" viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/><line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="2"/></svg>
              </button>
            </div>
          </fieldset>
        </div>
      );
    }

    if (!cfg) return null;

    return (
      <div className="options-panel" data-testid="options-panel">
        <fieldset className="control-fieldset buttons-fieldset">
          <legend>{cfg.legend}</legend>
          <div className="option-buttons">
            {cfg.items.map(item => (
              <button
                key={item.id}
                className={`option-btn ${cfg.selected === item.id ? 'active' : ''}`}
                data-testid={`${cfg.testPrefix}-btn-${item.id}`}
                onClick={() => cfg.onSelect(item.id)}
              >{item.name}</button>
            ))}
          </div>
        </fieldset>
      </div>
    );
  };

  /* ===================================================
     SLIDERS PANEL — rendered separately so it stays
     BELOW the canvas on mobile via CSS order.
     =================================================== */
  const renderSlidersPanel = () => {
    if (!activeTab) return null;

    if (activeTab === 'patterns') return (
      <div className="sliders-panel" data-testid="pattern-controls">
        <fieldset className="control-fieldset sliders-fieldset">
          <legend>pattern controls</legend>
          <Slider label="hue shift" min={0} max={360} value={patternHue} onDrag={h.patternHue.drag} onCommit={h.patternHue.commit} suffix="°" />
          <Slider label="opacity" min={0} max={100} value={patternOpacity} onDrag={h.patternOpacity.drag} onCommit={h.patternOpacity.commit} suffix="%" />
          <Slider label="scale horizontal" min={100} max={400} value={patternScaleX} onDrag={h.patternScaleX.drag} onCommit={h.patternScaleX.commit} suffix="%" />
          <Slider label="scale vertical" min={100} max={400} value={patternScaleY} onDrag={h.patternScaleY.drag} onCommit={h.patternScaleY.commit} suffix="%" />
        </fieldset>
      </div>
    );

    if (activeTab === 'overlay') return (
      <div className="sliders-panel" data-testid="overlay-controls">
        <fieldset className="control-fieldset sliders-fieldset">
          <legend>overlay controls</legend>
          <Slider label="hue shift" min={0} max={360} value={overlayHue} onDrag={h.overlayHue.drag} onCommit={h.overlayHue.commit} suffix="°" />
          <Slider label="opacity" min={0} max={100} value={overlayOpacity} onDrag={h.overlayOpacity.drag} onCommit={h.overlayOpacity.commit} suffix="%" />
          <Slider label="scale horizontal" min={100} max={400} value={overlayScaleX} onDrag={h.overlayScaleX.drag} onCommit={h.overlayScaleX.commit} suffix="%" />
          <Slider label="scale vertical" min={100} max={400} value={overlayScaleY} onDrag={h.overlayScaleY.drag} onCommit={h.overlayScaleY.commit} suffix="%" />
        </fieldset>
      </div>
    );

    if (activeTab === 'digi') return (
      <div className="sliders-panel" data-testid="digi-controls">
        <fieldset className="control-fieldset sliders-fieldset">
          <legend>digi controls</legend>
          <Slider label="hue shift" min={0} max={360} value={digiHue} onDrag={h.digiHue.drag} onCommit={h.digiHue.commit} suffix="°" />
          <Slider label="opacity" min={0} max={100} value={digiOpacity} onDrag={h.digiOpacity.drag} onCommit={h.digiOpacity.commit} suffix="%" />
          <Slider label="scale horizontal" min={100} max={400} value={digiScaleX} onDrag={h.digiScaleX.drag} onCommit={h.digiScaleX.commit} suffix="%" />
          <Slider label="scale vertical" min={100} max={400} value={digiScaleY} onDrag={h.digiScaleY.drag} onCommit={h.digiScaleY.commit} suffix="%" />
        </fieldset>
      </div>
    );

    if (activeTab === 'filters') return (
      <div className="sliders-panel" data-testid="filter-controls">
        <fieldset className="control-fieldset sliders-fieldset">
          <legend>filter controls</legend>
          <Slider label="hue shift" min={0} max={360} value={filterHue} onDrag={h.filterHue.drag} onCommit={h.filterHue.commit} suffix="°" />
          <Slider label="opacity" min={0} max={100} value={filterOpacity} onDrag={h.filterOpacity.drag} onCommit={h.filterOpacity.commit} suffix="%" />
        </fieldset>
      </div>
    );

    if (activeTab === 'collage') {
      if (collageMode === 'single') {
        return (
          <div className="sliders-panel" data-testid="collage-controls">
            <fieldset className="control-fieldset sliders-fieldset">
              <legend>collage controls</legend>
              <Slider label="horizontal scale" min={20} max={100} value={collageScaleX} onDrag={hCollage.scaleX.drag} onCommit={hCollage.scaleX.commit} suffix="%" />
              <Slider label="vertical scale" min={20} max={100} value={collageScaleY} onDrag={hCollage.scaleY.drag} onCommit={hCollage.scaleY.commit} suffix="%" />
              <Slider label="rotate" min={-180} max={180} value={collageRotate} onDrag={hCollage.rotate.drag} onCommit={hCollage.rotate.commit} suffix="°" />
            </fieldset>
          </div>
        );
      }
      const sideA = collageMode === 'horizontal' ? 'left' : 'top';
      const sideB = collageMode === 'horizontal' ? 'right' : 'bottom';
      return (
        <div className="sliders-panel" data-testid="collage-controls">
          <input type="file" ref={collageFileInputRef} onChange={handleCollageSecondUpload} accept="image/*" style={{ display: 'none' }} />
          <fieldset className="control-fieldset sliders-fieldset">
            <legend>collage controls</legend>
            <div className="control-group">
              <div className="option-buttons collage-upload-row">
                <button className="option-btn" data-testid="collage-upload-a" onClick={() => handleCollageUploadSide('a')}>
                  upload {sideA}
                </button>
                <button className="option-btn" data-testid="collage-upload-b" onClick={() => handleCollageUploadSide('b')}>
                  upload {sideB}
                </button>
              </div>
            </div>
            <Slider label="shift divider" min={0} max={100} value={collageDivider} onDrag={hCollage.divider.drag} onCommit={hCollage.divider.commit} suffix="%" />
            <div className="control-group">
              <label>pixelization</label>
              <div className="pixel-toggle-row">
                <button
                  className={`option-btn pixel-toggle-btn ${collagePixelA ? 'active' : ''}`}
                  data-testid="collage-pixel-a"
                  onClick={() => handleCollagePixelToggle('a')}
                >{sideA}</button>
                <button
                  className={`option-btn pixel-toggle-btn ${collagePixelB ? 'active' : ''}`}
                  data-testid="collage-pixel-b"
                  onClick={() => handleCollagePixelToggle('b')}
                >{sideB}</button>
              </div>
            </div>
            <div className="pixel-size-row">
              <div className={`pixel-size-half ${!collagePixelA ? 'disabled-slider' : ''}`}>
                <Slider label={`${sideA} px`} min={2} max={25} value={collagePixelSizeA} onDrag={collagePixelA ? hCollage.pixelSizeA.drag : undefined} onCommit={collagePixelA ? hCollage.pixelSizeA.commit : () => {}} />
              </div>
              <div className={`pixel-size-half ${!collagePixelB ? 'disabled-slider' : ''}`}>
                <Slider label={`${sideB} px`} min={2} max={25} value={collagePixelSizeB} onDrag={collagePixelB ? hCollage.pixelSizeB.drag : undefined} onCommit={collagePixelB ? hCollage.pixelSizeB.commit : () => {}} />
              </div>
            </div>
          </fieldset>
        </div>
      );
    }

    if (activeTab === 'adjust') return (
      <div className="sliders-panel adjust-section" data-testid="adjust-controls">
        <fieldset className="control-fieldset sliders-fieldset">
          <legend>image adjustments</legend>
          <div className="adjust-grid">
            <Slider label="exposure" min={-100} max={100} value={adjustments.exposure} onDrag={h.adj.exposure.drag} onCommit={h.adj.exposure.commit} dampened />
            <Slider label="brightness" min={-100} max={100} value={adjustments.brightness} onDrag={h.adj.brightness.drag} onCommit={h.adj.brightness.commit} dampened />
            <Slider label="contrast" min={-100} max={100} value={adjustments.contrast} onDrag={h.adj.contrast.drag} onCommit={h.adj.contrast.commit} dampened />
            <Slider label="pixelation" min={0} max={25} value={adjustments.pixelation} onDrag={h.adj.pixelation.drag} onCommit={h.adj.pixelation.commit} dampened />
            <Slider label="blur" min={0} max={100} value={adjustments.blur} onDrag={h.adj.blur.drag} onCommit={h.adj.blur.commit} dampened />
            <Slider label="vibrance" min={-100} max={100} value={adjustments.vibrance} onDrag={h.adj.vibrance.drag} onCommit={h.adj.vibrance.commit} dampened />
            <Slider label="saturation" min={-100} max={100} value={adjustments.saturation} onDrag={h.adj.saturation.drag} onCommit={h.adj.saturation.commit} dampened />
            <Slider label="warmth" min={-100} max={100} value={adjustments.warmth} onDrag={h.adj.warmth.drag} onCommit={h.adj.warmth.commit} dampened />
            <Slider label="tint" min={-100} max={100} value={adjustments.tint} onDrag={h.adj.tint.drag} onCommit={h.adj.tint.commit} dampened />
            <Slider label="sharpness" min={0} max={100} value={adjustments.sharpness} onDrag={h.adj.sharpness.drag} onCommit={h.adj.sharpness.commit} dampened />
            <Slider label="definition" min={-100} max={100} value={adjustments.definition} onDrag={h.adj.definition.drag} onCommit={h.adj.definition.commit} dampened />
            <Slider label="vignette" min={-100} max={100} value={adjustments.vignette} onDrag={h.adj.vignette.drag} onCommit={h.adj.vignette.commit} dampened />
          </div>
        </fieldset>
      </div>
    );

    return null;
  };

  /* ===================================================
     JSX — Retro Windows XP UI
     
     Mobile layout strategy (via CSS flex order):
       order 1: options-panel (filter/overlay choices) — TOP
       order 2: window-content (canvas preview) — MIDDLE
       order 3: sliders-panel (sliders/controls) — BOTTOM
     
     Desktop: natural source order (canvas → options → sliders below)
     =================================================== */
  return (
    <div className="desktop-background" data-testid="lightleakz-app">
      <div className="desktop-icons" data-testid="desktop-icons">
        {DESKTOP_ICONS.map((icon) => (
          <div className="desktop-icon" key={icon.alt}>
            <img src={icon.src} alt={icon.alt} className="icon-image" />
            <span>{icon.label}</span>
          </div>
        ))}
      </div>

      <div className={`xp-window ${isMaximized ? 'maximized' : ''}`} data-testid="main-window">
        <div className="title-bar">
          <div className="title-bar-left">
            <span className="window-icon">✨</span>
            <span className="window-title">lightleakz</span>
          </div>
          <div className="title-bar-buttons">
            <button className="title-btn minimize" data-testid="minimize-btn" onClick={() => setIsMaximized(false)}><span>_</span></button>
            <button className="title-btn maximize" data-testid="maximize-btn" onClick={() => setIsMaximized(m => !m)}><span>{isMaximized ? '❐' : '□'}</span></button>
            <button className="title-btn close" data-testid="close-btn" onClick={handleClose}><span>×</span></button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="toolbar" data-testid="toolbar">
          <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" style={{ display: "none" }} />

          {/* Desktop layout: open+download | tabs | reset */}
          <div className="toolbar-desktop">
            <div className="toolbar-left">
              <button className="toolbar-btn" data-testid="open-image-btn" onClick={() => fileInputRef.current?.click()}>open image</button>
              <button className="toolbar-btn" data-testid="download-btn" onClick={handleDownload} disabled={!baseImage || isExporting}>
                {isExporting ? 'saving...' : 'download'}
              </button>
            </div>
            <div className="toolbar-divider"></div>
            <div className="toolbar-center">
              <button className={`toolbar-btn ${activeTab === 'patterns' ? 'active-tab' : ''}`} data-testid="patterns-tab-btn" onClick={() => handleTabSwitch('patterns')} disabled={!baseImage}>patterns</button>
              <button className={`toolbar-btn ${activeTab === 'overlay' ? 'active-tab' : ''}`} data-testid="overlay-tab-btn" onClick={() => handleTabSwitch('overlay')} disabled={!baseImage}>overlays</button>
              <button className={`toolbar-btn ${activeTab === 'digi' ? 'active-tab' : ''}`} data-testid="digi-tab-btn" onClick={() => handleTabSwitch('digi')} disabled={!baseImage}>digi</button>
              <button className={`toolbar-btn ${activeTab === 'collage' ? 'active-tab' : ''}`} data-testid="collage-tab-btn" onClick={() => handleTabSwitch('collage')} disabled={!baseImage}>collage</button>
              <button className={`toolbar-btn ${activeTab === 'filters' ? 'active-tab' : ''}`} data-testid="filters-tab-btn" onClick={() => handleTabSwitch('filters')} disabled={!baseImage}>filters</button>
              <button className={`toolbar-btn ${activeTab === 'adjust' ? 'active-tab' : ''}`} data-testid="adjust-tab-btn" onClick={() => handleTabSwitch('adjust')} disabled={!baseImage}>edit</button>
            </div>
            <div className="toolbar-divider"></div>
            <div className="toolbar-right">
              <button className="toolbar-btn reset-btn" data-testid="reset-btn" onClick={handleReset} disabled={!baseImage}>clear</button>
            </div>
          </div>

          {/* Mobile layout: open+download left, reset right → divider → tabs */}
          <div className="toolbar-mobile">
            <div className="toolbar-mobile-actions">
              <button className="toolbar-btn" onClick={() => fileInputRef.current?.click()}>open image</button>
              <button className="toolbar-btn" onClick={handleDownload} disabled={!baseImage || isExporting}>
                {isExporting ? '...' : 'download'}
              </button>
              <span className="toolbar-mobile-spacer"></span>
              <button className="toolbar-btn reset-btn" onClick={handleReset} disabled={!baseImage}>clear</button>
            </div>
            <div className="toolbar-mobile-divider"></div>
            <div className="toolbar-mobile-tabs">
              <button className={`toolbar-btn ${activeTab === 'patterns' ? 'active-tab' : ''}`} onClick={() => handleTabSwitch('patterns')} disabled={!baseImage}>patterns</button>
              <button className={`toolbar-btn ${activeTab === 'overlay' ? 'active-tab' : ''}`} onClick={() => handleTabSwitch('overlay')} disabled={!baseImage}>overlays</button>
              <button className={`toolbar-btn ${activeTab === 'digi' ? 'active-tab' : ''}`} onClick={() => handleTabSwitch('digi')} disabled={!baseImage}>digi</button>
              <button className={`toolbar-btn ${activeTab === 'collage' ? 'active-tab' : ''}`} onClick={() => handleTabSwitch('collage')} disabled={!baseImage}>collage</button>
              <button className={`toolbar-btn ${activeTab === 'filters' ? 'active-tab' : ''}`} onClick={() => handleTabSwitch('filters')} disabled={!baseImage}>filters</button>
              <button className={`toolbar-btn ${activeTab === 'adjust' ? 'active-tab' : ''}`} onClick={() => handleTabSwitch('adjust')} disabled={!baseImage}>edit</button>
            </div>
          </div>
        </div>

        {/* Editor body — 3 direct children for flexible ordering */}
        <div className="editor-body">
          <div className="editor-options-panel">
            {renderOptionButtons()}
          </div>

          <div className="editor-canvas-section">
            <div className="window-content">
              <div className="canvas-container" data-testid="canvas-container">
                {!baseImage ? (
                  <div className="placeholder-container" onClick={() => fileInputRef.current?.click()} style={{ cursor: 'pointer' }}>
                    <div className="retro-placeholder">
                      <div className="placeholder-text">click "open image"</div>
                    </div>
                  </div>
                ) : (
                  <canvas
                    ref={canvasRef}
                    className={`main-canvas ${isCollageSingle ? 'collage-scale-mode' : ''}`}
                    data-testid="main-canvas"
                    style={{ width: effectiveDisplayDims.width, height: effectiveDisplayDims.height }}
                  />
                )}
              </div>
            </div>

            {baseImage && (
              <div className="action-bar" data-testid="action-bar">
                <div className="action-bar-left">
                  <button className="toolbar-btn action-bar-btn" data-testid="undo-btn" onClick={handleUndo} disabled={!canUndo}><span className="action-arrow">◀</span><span className="action-text"> back</span></button>
                  <button className="toolbar-btn action-bar-btn" data-testid="redo-btn" onClick={handleRedo} disabled={!canRedo}><span className="action-text">forward </span><span className="action-arrow">▶</span></button>
                </div>
                <div className="action-bar-center">
                  <button className="toolbar-btn action-bar-btn apply-btn" data-testid="apply-btn" onClick={handleApplyChanges} disabled={!canApply}>apply changes</button>
                </div>
                <div className="action-bar-right">
                  <button className="toolbar-btn action-bar-btn" data-testid="reset-tab-btn" onClick={handleResetCurrentTab} disabled={!activeTab}>reset</button>
                </div>
              </div>
            )}
          </div>

          <div className="editor-sliders-section">
            {renderSlidersPanel()}
          </div>
        </div>

        <div className="status-bar" data-testid="status-bar">
          <div className="status-section">{baseImage ? `${imageDimensions.width} × ${imageDimensions.height} px` : 'ready'}</div>
          <div className="status-section">{activeTab && `${activeTab} active`}</div>
          <div className="status-section">lightleakz v1.0</div>
        </div>
      </div>

      {/* Windows XP Taskbar */}
      <div className="taskbar" data-testid="taskbar">
        <button className="start-button" data-testid="start-button">
          <img src="/computer_explorer-4.png" alt="start" className="start-icon" />
          <span>Start</span>
        </button>
        <div className="taskbar-divider"></div>
        <div className="taskbar-apps">
          {baseImage && (
            <button className="taskbar-app active" data-testid="taskbar-lightleakz">
              <span className="taskbar-app-icon">✨</span>
              lightleakz
            </button>
          )}
        </div>
        <div className="taskbar-tray">
          <span className="tray-item">{clockTime}</span>
        </div>
      </div>
    </div>
  );
}

export default App;
